'use client';

import { useMemo, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Dropdown, DropdownOption } from '../../atoms/Dropdown/Dropdown';
import {
  CollapsiblePanel,
  CollapsiblePanelContent,
  CollapsiblePanelItem,
  CollapsiblePanelTrigger,
} from '../../molecules/CollapsiblePanel/CollapsiblePanel';
import { SheetPropsData } from '../../molecules/Sheets/Sheets.types';
import { SheetsStack } from '../../molecules/Sheets/SheetsStack';
import { ReleaseDetail } from '../ReleaseDetail/ReleaseDetail';
import { PackageRelease, ReleaseListProps } from './ReleaseList.types';

export const ReleaseList = ({
  packages,
  microcopy,
  className = '',
  onAccordionChange,
}: ReleaseListProps) => {
  const [selectedPackage, setSelectedPackage] = useState<DropdownOption | null>(
    packages.length
      ? { value: packages[0].title, label: packages[0].title }
      : null
  );

  const activePackage = useMemo(
    () =>
      selectedPackage
        ? packages.find((p) => p.title === selectedPackage.value) ?? packages[0]
        : packages[0],
    [packages, selectedPackage]
  );

  const dropdownOptions = useMemo(
    () => packages.map((pkg) => ({ value: pkg.title, label: pkg.title })),
    [packages]
  );

  const renderReleaseListDropdown = (packages: PackageRelease[]) => {
    return (
      <>
        {packages.length > 1 && (
          <Dropdown
            id="release-list-package"
            options={dropdownOptions}
            selectedOption={selectedPackage}
            onSelect={(opt) => setSelectedPackage(opt)}
            isDarkTheme={false}
            className="mt-6 md:mt-0"
          />
        )}
      </>
    );
  };

  const renderDesktopReleaseList = () => (
    <div className="hidden md:block">
      <h2 className="mb-8 text-md font-medium">{microcopy.title}</h2>

      {renderReleaseListDropdown(packages)}

      <CollapsiblePanel type="multiple" className="mt-8">
        {activePackage.releases.map((release, i) => (
          <CollapsiblePanelItem
            key={`${activePackage.title}-${release.version ?? release.released}`}
            value={`item-${i}`}
          >
            <CollapsiblePanelTrigger
              onValueChange={(value) => {
                onAccordionChange?.(
                  release.version || 'Unknown version',
                  value ? 'open' : 'close'
                );
              }}
            >
              <span>
                {release.version}{' '}
                {i === 0 && (
                  <span className="body-tiny text-grey-600">
                    {microcopy.current}
                  </span>
                )}
              </span>
            </CollapsiblePanelTrigger>
            <CollapsiblePanelContent>
              <ReleaseDetail {...activePackage.releases[i]} />
            </CollapsiblePanelContent>
          </CollapsiblePanelItem>
        ))}
      </CollapsiblePanel>
    </div>
  );

  const renderMobileReleaseList = () => {
    const mobileSheetDataWrapper: SheetPropsData[] = activePackage.releases.map(
      (release, i) => ({
        label: (
          <span>
            {release.version}{' '}
            {i === 0 && (
              <span className="body-tiny text-grey-600">
                {microcopy.current}
              </span>
            )}
          </span>
        ),
        children: <ReleaseDetail {...release} />,
        microcopy: microcopy.sheet,
      })
    );

    return (
      <div className="block md:hidden">
        {renderReleaseListDropdown(packages)}
        <SheetsStack
          sheets={mobileSheetDataWrapper}
          microcopy={microcopy.sheet}
          className={c('[&_p]:body-tiny mt-8 [&_p]:text-grey-700')}
        />
      </div>
    );
  };

  if (!packages.length) return null;

  return (
    <section className={c(className)}>
      {renderDesktopReleaseList()}
      {renderMobileReleaseList()}
    </section>
  );
};
